import {
  QueryClient,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { fetchVerifyUserEmail } from './service';
import { AxiosError, AxiosInstance } from 'axios';
import { UserEmailsQueryKeys } from '../../queryKeys';
import { useAxiosInstance, useModal } from '@starsoft/common/hooks';
import { CommonModalsKey } from '@starsoft/common/enums';
import { UserEmailVerifyInputDto } from '@starsoft/common/dto';
import { GenericError, Success } from '@starsoft/common/models';
import { useNotificationService } from '@starsoft/common/hooks/useNotificationService';
import { NotificationsService } from '@starsoft/common/services';

/**
 * Custom hook to verify user email addresses
 *
 * This hook handles the verification of user email addresses and manages the mutation state.
 * It uses React Query for data mutation, cache invalidation, and handles success notifications.
 * On successful verification, it closes the email modal, invalidates relevant queries, and shows a success message.
 *
 * @returns {Object} An object containing:
 *   - verifyUserEmail: Function to trigger email verification (non-async)
 *   - verifyUserEmailAsync: Function to trigger email verification (async version)
 *   - isPending: Boolean indicating if verification is in progress
 */
export function useVerifyUserEmail() {
  const queryClient: QueryClient = useQueryClient();
  const { onClose } = useModal(CommonModalsKey.UserEmail);
  const NotificationsService: NotificationsService<unknown> =
    useNotificationService();
  const axiosInstance: AxiosInstance = useAxiosInstance();

  /**
   * Success callback handler for email verification
   * Closes modal, invalidates queries, and shows success notification
   *
   * @param {Success} _ - Success response from the server (unused)
   * @param {UserEmailVerifyInputDto} data - The verification input data containing emailId
   */
  function onSuccess(_: Success, data: UserEmailVerifyInputDto) {
    onClose();
    queryClient.invalidateQueries({
      queryKey: [UserEmailsQueryKeys.Me],
    });
    queryClient.invalidateQueries({
      queryKey: [UserEmailsQueryKeys.MeById, data.emailId],
    });
    NotificationsService.success({
      title: 'verify_user_email_title',
      message: 'verify_user_email_description',
    });
  }

  const { isPending, mutate, mutateAsync } = useMutation<
    Success,
    AxiosError<GenericError>,
    UserEmailVerifyInputDto
  >({
    mutationFn: (data: UserEmailVerifyInputDto) =>
      fetchVerifyUserEmail(data, axiosInstance),
    onSuccess,
  });

  return {
    verifyUserEmail: mutate,
    isPending,
    verifyUserEmailAsync: mutateAsync,
  };
}
